import React, { useState } from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import {
  Search,
  PersonAdd,
  Visibility,
  Delete,
  Close,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  removeEmployee,
  addUser,
  createEmployeeAsync,
  removeEmployeeAsync,
} from "../../store/slices/dataSlice";
import { showSnackbar } from "../../store/slices/uiSlice";
import { Employee, User, ROLES_CONFIG } from "../../types";
import Avatar from "../../components/shared/Avatar";
import StatusBadge from "../../components/shared/StatusBadge";

const DEPTS = [
  "Engineering",
  "Sales",
  "Marketing",
  "Finance",
  "HR",
  "Operations",
];
const SYS_ROLES = [
  { v: "employee", l: "Employee" },
  { v: "manager", l: "Manager" },
  { v: "hr", l: "HR" },
  { v: "finance", l: "Finance" },
];

// Using global theme for table headers

const EmployeeProfile: React.FC<{
  emp: Employee;
  canViewSalary: boolean;
  onClose: () => void;
}> = ({ emp, canViewSalary, onClose }) => (
  <Dialog
    open
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: 3 } }}
  >
    <Box
      sx={{
        background: "linear-gradient(135deg,#85D1DB,#B6F2D1)",
        p: 3,
        display: "flex",
        alignItems: "center",
        gap: 2,
        position: "relative",
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "rgba(255,255,255,0.7)",
        }}
      >
        <Close />
      </IconButton>
      <Avatar initials={emp.initials} color={emp.avColor} size={56} />
      <Box>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>
          {emp.name}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>
          {emp.role}
        </Typography>
        <Typography
          sx={{ fontSize: 11, color: "rgba(255,255,255,0.6)", mt: 0.5 }}
        >
          {emp.dept} · {emp.id}
        </Typography>
      </Box>
    </Box>
    <DialogContent sx={{ p: 0 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
        {[
          ["Email", emp.email],
          ["Status", <StatusBadge status={emp.status} />],
          ["Date of Joining", emp.joined],
          [
            "Salary",
            canViewSalary ? `₹${emp.salary.toLocaleString("en-IN")}` : "••••••",
          ],
          ["Phone", emp.phone || "N/A"],
          ["Manager", emp.manager || "—"],
          ["Address", emp.address || "N/A"],
        ].map(([k, v], i) => (
          <Box
            key={i}
            sx={{
              p: "12px 16px",
              borderBottom: "1px solid #f1f5f9",
              borderRight: i % 2 === 0 ? "1px solid #f1f5f9" : "none",
              gridColumn: k === "Address" ? "1/-1" : "auto",
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.5,
              }}
            >
              {k}
            </Typography>
            <Typography sx={{ fontSize: 13 }}>{v}</Typography>
          </Box>
        ))}
      </Box>
    </DialogContent>
    <DialogActions sx={{ p: "12px 16px", borderTop: "1px solid #e5e7eb" }}>
      <Button onClick={onClose} variant="outlined">
        Close
      </Button>
      <Button variant="contained">Edit Profile</Button>
    </DialogActions>
  </Dialog>
);

const Employees: React.FC = () => {
  const dispatch = useAppDispatch();
  const employees = useAppSelector((s) => s.data.employees);
  const user = useAppSelector((s) => s.auth.user);
  const roleConfig = user
    ? ROLES_CONFIG[user.role.toLowerCase() as keyof typeof ROLES_CONFIG]
    : null;
  const canEdit = roleConfig?.canEditEmployee ?? false;
  const canViewSalary = roleConfig?.canViewSalary ?? false;

  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [viewEmp, setViewEmp] = useState<Employee | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    dept: "Engineering",
    role: "",
    joined: new Date().toISOString().split("T")[0],
    salary: "60000",
    sysRole: "employee",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const filtered = employees.filter(
    (e) =>
      (!search ||
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        e.role.toLowerCase().includes(search.toLowerCase()) ||
        e.id.toLowerCase().includes(search.toLowerCase())) &&
      (!deptFilter || e.dept === deptFilter) &&
      (!statusFilter || e.status === statusFilter),
  );

  const handleAdd = () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    if (!form.firstName) {
      newErrors.firstName = "First Name is required";
      isValid = false;
    }
    if (!form.lastName) {
      newErrors.lastName = "Last Name is required";
      isValid = false;
    }

    if (!form.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!form.password) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (form.password.length < 6) {
      newErrors.password = "Min 6 characters";
      isValid = false;
    }

    if (!form.phone) {
      newErrors.phone = "Phone number is required";
      isValid = false;
    } else if (!/^\d{10}$/.test(form.phone)) {
      newErrors.phone = "Must be 10 digits";
      isValid = false;
    }

    if (!form.role) {
      newErrors.role = "Designation is required";
      isValid = false;
    }

    if (!form.salary || parseInt(form.salary) <= 0) {
      newErrors.salary = "Must be > 0";
      isValid = false;
    }

    setErrors(newErrors);
    if (!isValid) return;

    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      password: form.password,
      phone: form.phone,
      dept: form.dept,
      role: form.role || "Employee",
      sysRole: form.sysRole,
      joined: form.joined,
      salary: parseInt(form.salary) || 60000,
    };

    dispatch(createEmployeeAsync(payload))
      .unwrap()
      .then(() => {
        dispatch(
          showSnackbar({
            message: "Employee and user account created successfully!",
          }),
        );
        setAddOpen(false);
        setForm({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          phone: "",
          dept: "Engineering",
          role: "",
          joined: new Date().toISOString().split("T")[0],
          salary: "60000",
          sysRole: "employee",
        });
        setErrors({});
      })
      .catch((err) => {
        dispatch(
          showSnackbar({
            message: err || "Failed to create employee",
            severity: "error",
          }),
        );
      });
  };

  const handleRemove = (emp: any) => {
    if (!window.confirm(`Are you sure you want to delete ${emp.name}? This will also delete their login account.`)) {
      return;
    }
    
    const idToDelete = emp._id || emp.id;
    dispatch(removeEmployeeAsync(idToDelete))
      .unwrap()
      .then(() => {
        dispatch(
          showSnackbar({ message: `${emp.name} removed successfully`, severity: "warning" }),
        );
      })
      .catch((err) => {
        dispatch(
          showSnackbar({ message: err || "Failed to delete employee", severity: "error" }),
        );
      });
  };

  return (
    <Box>
      <SEO title="Employees" noindex />
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
        }}
      >
        <TextField
          size="small"
          placeholder="Search employees..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Department</InputLabel>
          <Select
            value={deptFilter}
            label="Department"
            onChange={(e) => setDeptFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {DEPTS.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {["Active", "On Leave", "Inactive"].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setAddOpen(true)}
          >
            Add Employee
          </Button>
        )}
      </Box>

      <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 1.5 }}>
        Showing {filtered.length} of {employees.length} employees
      </Typography>

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 800, '& th, & td': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                {[
                  "Employee",
                  "Department",
                  "Role",
                  "Status",
                  "Joined",
                  "Salary",
                  "Actions",
                ].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((e) => (
                <TableRow
                  key={e.id}
                  sx={{
                    transition: "background 0.15s",
                    "&:hover": { background: "#fafbff" },
                  }}
                >
                  <TableCell
                    sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.2 }}
                    >
                      <Avatar
                        initials={e.initials}
                        color={e.avColor}
                        size={34}
                      />
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                          {e.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "#94a3b8",
                            fontFamily: "'JetBrains Mono',monospace",
                          }}
                        >
                          {e.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                  >
                    <StatusBadge status={e.dept} />
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 13,
                      color: "#64748b",
                    }}
                  >
                    {e.role}
                  </TableCell>
                  <TableCell
                    sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                  >
                    <StatusBadge status={e.status} />
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 12,
                      color: "#64748b",
                      fontFamily: "'JetBrains Mono',monospace",
                    }}
                  >
                    {e.joined}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 13,
                      fontFamily: "'JetBrains Mono',monospace",
                      fontWeight: 600,
                    }}
                  >
                    {canViewSalary
                      ? `₹${e.salary.toLocaleString("en-IN")}`
                      : "••••••"}
                  </TableCell>
                  <TableCell
                    sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility sx={{ fontSize: 14 }} />}
                        onClick={() => setViewEmp(e)}
                        sx={{
                          fontSize: 11,
                          height: 30,
                          px: 1.2,
                          borderRadius: 1.5,
                        }}
                      >
                        View
                      </Button>
                      {canEdit && (
                        <Tooltip title={`Remove ${e.name}`}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemove(e)}
                            sx={{
                              color: "#94a3b8",
                              "&:hover": { color: "#dc2626" },
                            }}
                          >
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{ textAlign: "center", py: 5, color: "#94a3b8" }}
                  >
                    No employees match your filters
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* View Profile */}
      {viewEmp && (
        <EmployeeProfile
          emp={viewEmp}
          canViewSalary={canViewSalary}
          onClose={() => setViewEmp(null)}
        />
      )}

      {/* Add Employee Dialog */}
      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonAdd sx={{ color: "#85D1DB" }} /> Add New Employee
          </Box>
          <IconButton onClick={() => setAddOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="First Name"
                value={form.firstName}
                onChange={(e) => {
                  setForm((f) => ({ ...f, firstName: e.target.value }));
                  if (errors.firstName)
                    setErrors((err) => ({ ...err, firstName: "" }));
                }}
                error={!!errors.firstName}
                helperText={errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Last Name"
                value={form.lastName}
                onChange={(e) => {
                  setForm((f) => ({ ...f, lastName: e.target.value }));
                  if (errors.lastName)
                    setErrors((err) => ({ ...err, lastName: "" }));
                }}
                error={!!errors.lastName}
                helperText={errors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => {
                  setForm((f) => ({ ...f, email: e.target.value }));
                  if (errors.email) setErrors((err) => ({ ...err, email: "" }));
                }}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Password"
                type="password"
                value={form.password}
                onChange={(e) => {
                  setForm((f) => ({ ...f, password: e.target.value }));
                  if (errors.password)
                    setErrors((err) => ({ ...err, password: "" }));
                }}
                error={!!errors.password}
                helperText={errors.password}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Phone"
                value={form.phone}
                onChange={(e) => {
                  setForm((f) => ({ ...f, phone: e.target.value }));
                  if (errors.phone) setErrors((err) => ({ ...err, phone: "" }));
                }}
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={form.dept}
                  label="Department"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, dept: e.target.value }))
                  }
                >
                  {DEPTS.map((d) => (
                    <MenuItem key={d} value={d}>
                      {d}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Designation"
                value={form.role}
                onChange={(e) => {
                  setForm((f) => ({ ...f, role: e.target.value }));
                  if (errors.role) setErrors((err) => ({ ...err, role: "" }));
                }}
                placeholder="Senior Developer"
                error={!!errors.role}
                helperText={errors.role}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Date of Joining"
                type="date"
                value={form.joined}
                onChange={(e) =>
                  setForm((f) => ({ ...f, joined: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Salary (₹/month)"
                type="number"
                value={form.salary}
                onChange={(e) => {
                  setForm((f) => ({ ...f, salary: e.target.value }));
                  if (errors.salary)
                    setErrors((err) => ({ ...err, salary: "" }));
                }}
                error={!!errors.salary}
                helperText={errors.salary}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>System Role</InputLabel>
                <Select
                  value={form.sysRole}
                  label="System Role"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, sysRole: e.target.value }))
                  }
                >
                  {SYS_ROLES.map((r) => (
                    <MenuItem key={r.v} value={r.v}>
                      {r.l}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: "12px 24px", borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={() => setAddOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleAdd} variant="contained">
            Add Employee
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Employees;
